<template>
	<div>
		<!-- Submenu -->
		<client-submenu />

		<core-layout-content full>
			<!-- Loading -->
			<j-loading-toast v-if="isLoading" />

			<!-- Content -->
			<div class="flex wrap xs-12">
				<!-- Left Column -->
				<div class="xs-12 md-7">
					<core-entity-card
						:entity="entity"
						:pinnable="false"
						:hasActions="true"
						@added="fetchClient"
						@updated="fetchClient"
						@deleted="fetchClient"
					/>

					<digital-broker-source-section
						v-if="
							['companyCover_uat', 'digitalBroker'].includes(
								$store.state.tenantId
							)
						"
						class="mt6"
						:entity="entity"
						@input="fetchClient"
					/>
				</div>

				<!-- Right Column -->
				<div class="xs-12 md-5 md-pl12">
					<!-- Info -->
					<core-info-card :data="computedInfo" :hasStatus="false" />

					<!-- Notes -->
					<core-notes-card
						class="mt6"
						:notesOf="clientType"
						:id="id"
						:list="computedNotes"
						:canWrite="canWriteClientNotes"
						@note:created="fetchClient"
						@note:updated="fetchClient"
						@note:deleted="fetchClient"
					/>

					<!-- Files -->
					<core-files-card
						v-if="tenantsUsedOldFiles.includes($store.state.tenantId)"
						class="mt6"
						:id="id"
						:filesOf="clientType"
						:list="computedAttachments"
						:canWrite="canWriteFiles"
						@file:deleted="fetchClient"
						@files:uploaded="fetchClient"
					/>

					<core-files-card-new
						v-if="!tenantsUsedOldFiles.includes($store.state.tenantId)"
						class="mt6"
						:id="id"
						:filesOf="clientType"
						:list="files"
						:canWrite="canWriteFiles"
						@file:deleted="fetchFiles"
						@files:uploaded="fetchFiles"
					/>

					<!-- Invite based on tenant settings -->
					<j-card
						class="xs-12 bg5 r2 p4 mt6"
						v-if="$store.state.settings.clients.hasInviteClient"
					>
						<div class="fs5 lh125 fw7 mb1">{{ $t("invite_client") }}</div>
						<div class="lh125 c6">{{ $t("invite_client_description") }}</div>
						<j-button
							@click="$modal.show('inviteClient')"
							class="mt4"
							type="bordered"
							>{{ $t("invite") }}</j-button
						>

						<!-- Modal to invite -->
						<core-modal name="inviteClient" :headline="$t('invite')">
							<invite-client
								:id="id"
								@invite:finished="$modal.hide('inviteClient')"
							/>
						</core-modal>
					</j-card>

					<!-- MOCKED: 
						- specific invite for HKECIC only
					-->
					<hkecic-invite-client
						:entity="entity"
						v-if="
							$store.state.tenantId === 'hkecic_uat' ||
								$store.state.tenantId === 'hkecic'
						"
					/>
				</div>

				<!-- Footer menu -->
				<div class="flex wrap xs-12 xs-right bt1 bc7 pt2 mt8">
					<j-button
						type="error"
						@click="$modal.show('j-dialog')"
						v-can:or="['writeIndividuals', 'writeCompanies']"
						action="showDeleteClientModal"
					>
						<crm-icon name="delete" size="normal" color="c5" class="mr1" />
						{{ $t("delete_client") }}
					</j-button>
				</div>

				<!-- Delete Confirmation Modal -->
				<j-dialog
					:title="$t('delete_client')"
					type="delete"
					@confirm="onDelete(id)"
					>{{ $t("errors.are_you_sure_you_want_to_delete_client") }}</j-dialog
				>
			</div>

			<!-- Modals for create / update -->
			<router-view />
		</core-layout-content>
	</div>
</template>

<script>
import CoreLayoutContent from "@/modules/core/components/CoreLayoutContent"
import CoreEntityCard from "@/modules/core/components/CoreEntityCard/"
import JButton from "@/components/JButton/"
import JCard from "@/components/JCard/"
import JDialog from "@/components/JDialog/"
import CrmIcon from "@/components/CrmIcon/"
import JLoadingToast from "@/components/JLoadingToast/"
import ClientSubmenu from "../components/ClientSubmenu"
import CoreInfoCard from "@/modules/core/components/CoreInfoCard"
import CoreNotesCard from "@/modules/core/components/CoreNotesCard/index.vue"
import CoreFilesCard from "@/modules/core/components/CoreFilesCard"
import CoreFilesCardNew from "@/modules/core/components/CoreFilesCardNew"
import InviteClient from "../components/InviteClient"
import DigitalBrokerSourceSection from "../components/mockedDigitalBroker/DigitalBrokerSourceSection"
import CoreModal from "@/modules/core/components/CoreModal"

import globalApi from "@/api/gql.js"
import { gql } from "@/api/utils.js"
import api from "../api/"
import { can } from "@/permissions/"

// MOCKED: cms component for HKECIC
import HkecicInviteClient from "../components/mockedHKECIC/HkecicInviteClient"

export default {
	components: {
		CoreLayoutContent,
		CoreEntityCard,
		JButton,
		JCard,
		CrmIcon,
		JDialog,
		JLoadingToast,
		ClientSubmenu,
		CoreInfoCard,
		CoreNotesCard,
		CoreFilesCard,
		CoreFilesCardNew,
		InviteClient,
		CoreModal,

		// MOCK: cms
		DigitalBrokerSourceSection,
		HkecicInviteClient
	},
	props: {
		id: {
			type: String
		},
		section: {
			type: String,
			default: "overview"
		},
		clientType: {
			type: String,
			default: "individual", // or company
			required: true
		}
	},
	data() {
		return {
			isLoading: false,
			entity: {},
			files: []
		}
	},
	computed: {
		tenantsUsedOldFiles() {
			return ["apex", "apex_uat", "asia", "asia_uat", "vhis_uat", "vhis"]
		},
		computedInfo() {
			return {
				createdAt: this.entity.createdAt,
				createdBy: this.entity.createdBy,
				lastModifiedAt: this.entity.lastModifiedAt,
				lastModifiedBy: this.entity.lastModifiedBy
			}
		},
		computedNotes() {
			return this.entity.notes ? this.entity.notes : []
		},
		computedAttachments() {
			return this.entity.attachments ? this.entity.attachments : []
		},
		canWriteClientNotes() {
			return this.clientType === "individual"
				? can("writeIndividualNotes")
				: can("writeCompanyNotes")
		},
		canWriteFiles() {
			return can("writeFiles")
		}
	},
	methods: {
		async onDelete(id) {
			this.isLoading = true
			const res = await api.deleteCustomer({ id })
			this.isLoading = false
			// Error
			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
			}
			// go back to previous page
			if (res) this.$router.push(`/${this.$store.state.tenantId}/clients/`)
			this.$store.dispatch("addMessage", {
				message: {
					type: "success",
					text: this.$t("client_removed")
				}
			})
		},
		async fetchFiles() {
			const query = gql`
				query fileListing($prefix: String, $bucketName: String) {
					fileListing(prefix: $prefix, bucketName: $bucketName) {
						commonPrefixes
						objectSummaries {
							fileName
							key
							lastModifiedAt
							size
							isPublic
						}
					}
				}
			`
			const bucketNameMapping = {
				lapetus_uat: "lapetus--uat",
				companyCover_uat: "companycover--uat"
			}
			const getPrefix = (id, tenantId) => {
				const prefixSources = {
					/** Custom prefix can configure here */
					lapetus_uat: `entities/${id}/attachments/`
				}
				return prefixSources[tenantId] || `entities/${id}/attachments/`
			}
			const variables = {
				prefix: getPrefix(this.id),
				bucketName: bucketNameMapping[this.$store.state.tenantId]
			}
			const res = await globalApi({
				query,
				variables
			})

			this.files = res?.data?.fileListing?.objectSummaries || []
		},
		async fetchClient() {
			let where = { id: this.id }
			let apiKeys = null
			if (this.clientType === "individual")
				apiKeys = {
					request: "individual",
					result: "individuals"
				}
			else apiKeys = { request: "company", result: "companies" }
			this.isLoading = true
			const res = await api[apiKeys.request]({ where })
			this.isLoading = false
			// If not getting the detail data, jsut return
			if (!res?.data?.[apiKeys.result]?.list?.[0]) return
			// Error
			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
				return
			}
			this.entity = res.data[apiKeys.result].list[0]
		}
	},
	mounted() {
		this.fetchClient()
		this.fetchFiles()
	}
}
</script>
